import React, { lazy, Suspense, useEffect } from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'

import {
  CssBaseline,
  MuiThemeProvider as ThemeProvider,
} from '@material-ui/core'
import Theme from './Theme'
import MaintenanceIcon from '@material-ui/icons/SyncDisabledOutlined'

import { AppProvider } from './contexts/AppContext'
import CustomBrowserRouter from './utils/CustomBrowserRouter'
import SegmentPageTracker from 'utils/SegmentPageTracker'
import PrivateRoute from './utils/PrivateRoute'

import { SnackProvider } from 'samosas'

import GlobalStyles from './utils/GlobalStyles'
import HomeRedirect from './utils/HomeRedirect'
import { routes } from './constants/routes'

import {
  ErrorBoundary,
  Loading,
  EmptyState,
} from '@antlerengineering/components'
import Navigation from './components/Navigation'

import ForgotPasswordView from 'views/AuthViews/ForgotPasswordView'
import ResetPasswordView from 'views/AuthViews/ResetPasswordView'
import AuthLinkView from 'views/AuthViews/AuthLinkView'
import GoogleAuthView from 'views/AuthViews/GoogleAuthView'
import SignInView from 'views/AuthViews/SignInView'
import SignOutView from './views/SignOutView'
import AdminAuthView from 'views/AuthViews/AdminAuthView'
import RedirectView from 'views/RedirectView'
const HomeView = lazy(() =>
  import(
    /* webpackPrefetch: true */
    /* webpackChunkName: "HomeView" */
    './views/HomeView'
  )
)
const ProfileView = lazy(() =>
  import(
    /* webpackPrefetch: true */
    /* webpackChunkName: "ProfileView" */
    './views/ProfileView'
  )
)
const CohortView = lazy(() =>
  import(
    /* webpackPrefetch: true */
    /* webpackChunkName: "CohortView" */
    './views/CohortView'
  )
)
const CourseView = lazy(() =>
  import(
    /* webpackPrefetch: true */
    /* webpackChunkName: "CourseView" */
    './views/CourseView'
  )
)
const LibraryView = lazy(() =>
  import(
    /* webpackPrefetch: true */
    /* webpackChunkName: "LibraryView" */
    './views/LibraryView'
  )
)

// set to true during maintenance and redeploy
const MAINTENANCE_MODE = false

const App: React.FC = () => {
  if (MAINTENANCE_MODE)
    return (
      <ThemeProvider theme={Theme}>
        <EmptyState
          fullScreen
          Icon={MaintenanceIcon}
          message="Scheduled Maintenance"
          description={
            <>
              antler launch academy is currently under maintenance.
              <br />
              Please come back later.
            </>
          }
        />
      </ThemeProvider>
    )

  return (
    <ThemeProvider theme={Theme}>
      <CssBaseline />
      <GlobalStyles />
      <ErrorBoundary>
        <AppProvider>
          <SnackProvider>
            <CustomBrowserRouter>
              <SegmentPageTracker />
              <Suspense fallback={<Loading fullScreen />}>
                <Switch>
                  <Route
                    exact
                    path={routes.adminAuth}
                    render={() => <AdminAuthView />}
                  />
                  <Route
                    exact
                    path={routes.authLink}
                    render={() => <AuthLinkView />}
                  />
                  <Route
                    exact
                    path={routes.googleAuth}
                    render={() => <GoogleAuthView />}
                  />
                  <Route
                    exact
                    path={routes.forgotPassword}
                    render={() => <ForgotPasswordView />}
                  />
                  <Route
                    exact
                    path={routes.resetPassword}
                    render={() => <ResetPasswordView />}
                  />
                  <Route
                    exact
                    path={routes.signIn}
                    render={() => <SignInView />}
                  />

                  <Route
                    exact
                    path={routes.signOut}
                    render={() => <SignOutView />}
                  />

                  <PrivateRoute
                    exact
                    path={'/'}
                    render={() => (
                      <Navigation>
                        <HomeView />
                      </Navigation>
                    )}
                  />
                  <PrivateRoute
                    exact
                    path={'/home'}
                    render={() => (
                      <Navigation>
                        <HomeView />
                      </Navigation>
                    )}
                  />
                  <PrivateRoute
                    exact
                    path={routes.profile}
                    render={() => <ProfileView />}
                  />
                  <PrivateRoute
                    exact
                    path={routes.cohort}
                    render={() => (
                      <Navigation>
                        <CohortView />
                      </Navigation>
                    )}
                  />
                  <PrivateRoute
                    exact
                    path={routes.course}
                    render={() => (
                      <Navigation>
                        <CourseView />
                      </Navigation>
                    )}
                  />
                  <PrivateRoute
                    exact
                    path={routes.library}
                    render={() => (
                      <Navigation>
                        <LibraryView />
                      </Navigation>
                    )}
                  />

                  <PrivateRoute
                    exact
                    path={routes.redirect}
                    render={() => (
                      // <Navigation>
                      <RedirectView />
                      // </Navigation>
                    )}
                  />
                  <PrivateRoute
                    render={() => (
                      <Navigation>
                        <EmptyState message="Page Not Found" />
                      </Navigation>
                    )}
                  />
                </Switch>
              </Suspense>
            </CustomBrowserRouter>
          </SnackProvider>
        </AppProvider>
      </ErrorBoundary>
    </ThemeProvider>
  )
}

export default App
